import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import SkeletonGrid from "@/components/feature/SkeletonGrid";
import { popularArticles, searchContent, videos, articles } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

type ResultType = "all" | "article" | "video";
type ResultSort = "relevance" | "latest" | "hot";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [draft, setDraft] = useState(keyword);
  const [type, setType] = useState<ResultType>("all");
  const [sort, setSort] = useState<ResultSort>("relevance");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(keyword);

  useSeo({
    title: keyword ? `搜索“${keyword}”结果 - 抖阳` : "搜索 - 抖阳",
    description: keyword
      ? `在抖阳搜索“${keyword}”，结果涵盖原创文章与视频内容，可按类型与时间排序查看。`
      : "在抖阳搜索原创文章与视频内容，覆盖文化、娱乐、生活与观点四个频道。",
    keywords: `抖阳搜索,${keyword || "原创内容"},文章搜索,视频搜索`,
  });

  useEffect(() => {
    setDraft(keyword);
    setSubmitted(keyword);
    setLoading(Boolean(keyword));
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const result = useMemo(() => searchContent(keyword), [keyword]);

  const articleList = useMemo(() => {
    const list = [...result.articles];
    if (sort === "latest") list.sort((a, b) => b.date.localeCompare(a.date));
    if (sort === "hot") list.sort((a, b) => b.views - a.views);
    return list;
  }, [result.articles, sort]);

  const videoList = useMemo(() => {
    const list = [...result.videos];
    if (sort === "latest") list.sort((a, b) => b.date.localeCompare(a.date));
    if (sort === "hot") list.sort((a, b) => b.views - a.views);
    return list;
  }, [result.videos, sort]);

  const shownArticles = type === "video" ? [] : articleList;
  const shownVideos = type === "article" ? [] : videoList;
  const total = shownArticles.length + shownVideos.length;

  const submit = (value: string) => {
    const query = value.trim();
    if (!query) return;
    setParams({ q: query });
  };

  const hot = popularArticles(4);
  const hotVideos = videos.slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100/70 px-4 pb-8 pt-5 md:px-6 md:pb-10">
          <div className="mx-auto max-w-[900px]">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              全站搜索
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              输入关键词，检索抖阳的文章、视频与标签。演示数据，结果实时计算。
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(draft);
              }}
              className="mt-5 flex flex-col gap-2 sm:flex-row"
            >
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="搜索文章、视频或标签，如「城市」「短剧」"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {draft ? (
                  <button
                    type="button"
                    onClick={() => setDraft("")}
                    aria-label="清空关键词"
                    className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-line" />
                  </button>
                ) : null}
              </div>
              <button
                type="submit"
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                搜索
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-foreground-600">
              {keyword ? (
                <>
                  关键词「<span className="font-medium text-foreground-950">{submitted}</span>」共找到{" "}
                  <span className="font-editorial font-bold text-primary-600">{total}</span> 条结果
                  <span className="ml-2 text-xs text-foreground-400">
                    （文章 {articleList.length} · 视频 {videoList.length}）
                  </span>
                </>
              ) : (
                "输入关键词开始搜索，或浏览下方的热门内容。"
              )}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {[
                  { key: "all" as ResultType, label: "全部" },
                  { key: "article" as ResultType, label: "文章" },
                  { key: "video" as ResultType, label: "视频" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setType(item.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                      type === item.key
                        ? "bg-background-50 text-primary-600"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as ResultSort)}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 focus:border-primary-400 focus:outline-none"
              >
                <option value="relevance">按相关度</option>
                <option value="latest">按最新发布</option>
                <option value="hot">按阅读热度</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            {!keyword ? (
              <EmptyState
                icon="ri-search-eye-line"
                title="还没有输入关键词"
                description="试试「城市」「短剧」「厨房」「黑胶」等词，或者直接浏览下面的热门内容。"
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    {["城市", "短剧", "厨房", "黑胶"].map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => submit(tag)}
                        className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                }
              />
            ) : loading ? (
              <SkeletonGrid count={4} columns="sm:grid-cols-2 lg:grid-cols-4" />
            ) : total === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title="未找到相关内容"
                description={`没有与「${keyword}」匹配的文章或视频。可以换个关键词，或从下面的热门内容开始浏览。`}
                action={
                  <button
                    type="button"
                    onClick={() => {
                      setParams({});
                      setDraft("");
                    }}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
                  >
                    清空关键词
                  </button>
                }
              />
            ) : (
              <div className="space-y-8">
                {shownArticles.length > 0 ? (
                  <div>
                    <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                      <i className="ri-file-text-line text-primary-500" />
                      文章结果
                      <span className="text-xs font-normal text-foreground-400">{shownArticles.length} 篇</span>
                    </h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      {shownArticles.map((article) => (
                        <ArticleCard key={article.id} article={article} showSummary={false} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {shownVideos.length > 0 ? (
                  <div>
                    <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                      <i className="ri-play-circle-line text-primary-500" />
                      视频结果
                      <span className="text-xs font-normal text-foreground-400">{shownVideos.length} 个</span>
                    </h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      {shownVideos.map((video) => (
                        <VideoCard key={video.id} video={video} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-12">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-5 flex items-end justify-between gap-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">热门推荐</h2>
              <span className="text-xs text-foreground-400">按阅读与播放热度排序</span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2 rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="mb-1 flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-fire-line text-primary-500" />
                  热门文章
                </p>
                {hot.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="minimal" />
                ))}
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="mb-2 flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-play-circle-line text-primary-500" />
                  热门视频
                </p>
                <div className="space-y-1">
                  {hotVideos.map((video) => (
                    <VideoCard key={video.id} video={video} variant="horizontal" />
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 border-t border-background-200 pt-3">
                  {["文化", "娱乐", "生活", "观点"].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => submit(tag)}
                      className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                    >
                      {tag}
                    </button>
                  ))}
                  <Link
                    to={`/category/${articles[0]?.category ?? "culture"}`}
                    className="ml-auto flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                  >
                    浏览分类
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}